/**
 * Type definitions for ADA Toilet Side Wall Clearance Rule
 * IBC 604.2: Toilet centerline must be 16" min to 18" max from side wall
 */

/**
 * User-configurable parameters for ADA toilet validation
 */
export interface ADAToiletRuleParams {
  /** Text string to identify ADA toilets (case-insensitive contains match) */
  adaIdentifier: string;
  
  /** Minimum required distance from toilet centerline to side wall (inches) */
  minDistance: number;
  
  /** Maximum allowed distance from toilet centerline to side wall (inches) */
  maxDistance: number;
  
  /** Warning zone tolerance (inches) - creates warning bands at min/max boundaries */
  warningZone: number;
}

/**
 * Result of validating a single toilet
 */
export interface ToiletValidationResult {
  /** Database ID of the toilet element */
  toiletDbId: number;
  
  /** Name of the toilet element from model */
  toiletName: string;
  
  /** Database ID of the measured side wall (null if no wall found) */
  sideWallDbId: number | null;
  
  /** Name of the side wall element */
  sideWallName?: string;
  
  /** Measured distance from toilet centerline to side wall (inches) */
  measuredDistance: number | null;
  
  /** Validation status */
  status: 'pass' | 'warning' | 'fail' | 'error';
  
  /** Human-readable message describing the result */
  message: string;
  
  /** Additional details for debugging */
  details: {
    /** Distance to rear wall (inches) */
    rearWallDistance?: number;
    /** Number of walls found nearby */
    nearbyWallsCount?: number;
    /** Error details if applicable */
    error?: string;
    /** Unit conversion factors used */
    unitConversion?: {
      modelToFeet: number;
      feetToInches: number;
    };
    /** All side wall database IDs for visualization */
    allSideWallIds?: number[];
    /** All side wall names for visualization */
    allSideWallNames?: string[];
    /** Rear wall database ID for visualization */
    rearWallId?: number;
    /** Rear wall name for visualization */
    rearWallName?: string;
  };
}

/**
 * Intermediate data structure for wall analysis
 */
export interface WallAnalysis {
  /** Database ID of the wall */
  dbId: number;
  
  /** Name of the wall element */
  name: string;
  
  /** Wall bounding box */
  boundingBox: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
  
  /** Distance from toilet center to wall surface (feet) */
  surfaceDistance: number;
  
  /** Distance from toilet centerline to wall (feet) */
  centerlineDistance: number;
  
  /** Wall orientation vector (normalized) */
  orientation: { x: number; y: number; z: number };
  
  /** Angle relative to rear wall (degrees) */
  angleToRearWall?: number;
  
  /** Classification of wall type */
  wallType?: 'rear' | 'side' | 'front' | 'other';
}

/**
 * Overall validation report for all toilets
 */
export interface ADAToiletValidationReport {
  /** Timestamp of validation run */
  timestamp: string;
  
  /** Parameters used for validation */
  parameters: ADAToiletRuleParams;
  
  /** Individual toilet results */
  results: ToiletValidationResult[];
  
  /** Summary statistics */
  summary: {
    /** Total number of ADA toilets found */
    totalToilets: number;
    /** Number of toilets that passed */
    passed: number;
    /** Number of toilets with warnings */
    warnings: number;
    /** Number of toilets that failed */
    failed: number;
    /** Number of toilets with errors */
    errors: number;
    /** Overall compliance percentage */
    complianceRate: number;
  };
  
  /** Performance metrics */
  performance: {
    /** Total execution time (ms) */
    totalTime: number;
    /** Time per toilet (ms) */
    avgTimePerToilet: number;
  };
}

/**
 * Configuration for the ADA toilet rule in the rules engine
 */
export interface ADAToiletRuleConfig {
  /** Unique rule identifier */
  id: string;
  
  /** Display name for the rule */
  name: string;
  
  /** Rule category */
  category: 'Accessibility' | 'ADA Compliance';
  
  /** Detailed description */
  description: string;
  
  /** Reference to regulation */
  regulation: string;
  
  /** Whether rule is currently active */
  isActive: boolean;
  
  /** Rule parameters */
  parameters: ADAToiletRuleParams;
  
  /** Default parameter values */
  defaults: ADAToiletRuleParams;
}

/**
 * Unit conversion constants
 */
export const UNIT_CONVERSION = {
  /** Convert inches to feet */
  INCHES_TO_FEET: 0.0833333,
  /** Convert feet to inches */
  FEET_TO_INCHES: 12,
  /** Convert feet to meters */
  FEET_TO_METERS: 0.3048,
  /** Convert meters to feet */
  METERS_TO_FEET: 3.28084,
} as const;

/**
 * Default parameter values per IBC 604.2
 */
export const ADA_TOILET_DEFAULTS: ADAToiletRuleParams = {
  adaIdentifier: 'Unhousted1',
  minDistance: 16, // inches
  maxDistance: 18, // inches
  warningZone: 0.5, // inches
};